import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TrendingUp, TrendingDown, DollarSign, Percent, Calendar, ToggleLeft, ToggleRight, X } from 'lucide-react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { formatCurrency, formatDate, getLocalDateString } from '@/lib/utils';
import { toast } from 'sonner';
import api from '@/lib/api';
import { HelpTip } from '@/components/HelpTip';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('total');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showDateRange, setShowDateRange] = useState(false);

  const [pendingValue, setPendingValue] = useState('');
  const [operationalMode, setOperationalMode] = useState(false);
  const pendingAmount = parseFloat(pendingValue) || 0;

  const SAQUES_CATEGORY = 'Saques ISA e CAIO';
  const saquesTotal = useMemo(() => {
    if (!stats?.expenses_by_category) return 0;
    return stats.expenses_by_category
      .filter(c => c.exclude_from_operational)
      .reduce((sum, c) => sum + c.value, 0);
  }, [stats]);

  const excludedCategories = useMemo(() => {
    if (!stats?.expenses_by_category) return [];
    return stats.expenses_by_category.filter(c => c.exclude_from_operational);
  }, [stats]);

  const displayExpense = operationalMode ? (stats?.total_expense || 0) - saquesTotal : (stats?.total_expense || 0);
  const adjustedIncome = (stats?.total_income || 0) + pendingAmount;
  const adjustedProfit = adjustedIncome - displayExpense;
  const adjustedMargin = adjustedIncome > 0 ? (adjustedProfit / adjustedIncome) * 100 : 0;

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const params = { period: selectedPeriod };
      if (selectedPeriod === 'custom' && dateFrom && dateTo) {
        params.date_from = dateFrom;
        params.date_to = dateTo;
      }
      const response = await api.get('/dashboard/stats', { params });
      setStats(response.data);
    } catch (error) {
      toast.error('Erro ao carregar estatísticas');
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod, dateFrom, dateTo]);

  useEffect(() => {
    if (selectedPeriod === 'custom') {
      if (dateFrom && dateTo) loadStats();
    } else {
      loadStats();
    }
  }, [loadStats, selectedPeriod, dateFrom, dateTo]);

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    if (period === 'custom') {
      setShowDateRange(true);
      if (!dateFrom) setDateFrom(getLocalDateString());
      if (!dateTo) setDateTo(getLocalDateString());
    } else {
      setShowDateRange(false);
    }
  };

  const getFilterLabel = () => {
    if (selectedPeriod === 'custom' && dateFrom && dateTo) {
      return `${formatDate(dateFrom)} - ${formatDate(dateTo)}`;
    }
    return '';
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-muted-foreground">Carregando...</div>
      </div>
    );
  }

  const CHART_COLORS = {
    income: '#10B981',
    expense: '#F43F5E',
    profit: '#6366F1',
  };

  return (
    <div data-testid="dashboard-page">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-4xl font-bold text-foreground mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Dashboard
        </h1>
        <p className="text-sm sm:text-base text-muted-foreground">Visão geral do seu negócio</p>
      </div>

      {/* Operational Mode Toggle */}
      <div className="mb-4">
        <button
          onClick={() => setOperationalMode(!operationalMode)}
          data-testid="operational-mode-toggle"
          className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
            operationalMode
              ? 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400'
              : 'bg-muted text-muted-foreground hover:bg-accent'
          }`}
        >
          {operationalMode ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
          Modo Lucro Operacional
          {operationalMode && <span className="text-xs opacity-75">(ativo)</span>}
        </button>
        <HelpTip text="Ativa uma simulação que desconsidera retiradas pessoais (saques) dos seus gastos, mostrando apenas os custos reais do negócio." className="ml-1" />
      </div>

      {/* Filters */}
      <div className="mb-6" data-testid="dashboard-filters">
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 mb-4">
          <Button
            variant={selectedPeriod === 'total' ? 'default' : 'outline'}
            onClick={() => handlePeriodChange('total')}
            data-testid="filter-total"
            size="sm"
            className={`text-xs sm:text-sm ${selectedPeriod === 'total' ? 'bg-indigo-600 hover:bg-indigo-700' : ''}`}
          >
            Período Total
          </Button>
          <Button
            variant={selectedPeriod === 'today' ? 'default' : 'outline'}
            onClick={() => handlePeriodChange('today')}
            data-testid="filter-today"
            size="sm"
            className={`text-xs sm:text-sm ${selectedPeriod === 'today' ? 'bg-indigo-600 hover:bg-indigo-700' : ''}`}
          >
            Hoje
          </Button>
          <Button
            variant={selectedPeriod === 'last_7_days' ? 'default' : 'outline'}
            onClick={() => handlePeriodChange('last_7_days')}
            data-testid="filter-7-days"
            size="sm"
            className={`text-xs sm:text-sm ${selectedPeriod === 'last_7_days' ? 'bg-indigo-600 hover:bg-indigo-700' : ''}`}
          >
            7 dias
          </Button>
          <Button
            variant={selectedPeriod === 'last_30_days' ? 'default' : 'outline'}
            onClick={() => handlePeriodChange('last_30_days')}
            data-testid="filter-30-days"
            size="sm"
            className={`text-xs sm:text-sm ${selectedPeriod === 'last_30_days' ? 'bg-indigo-600 hover:bg-indigo-700' : ''}`}
          >
            30 dias
          </Button>
          <Button
            variant={selectedPeriod === 'custom' ? 'default' : 'outline'}
            onClick={() => handlePeriodChange('custom')}
            data-testid="filter-custom"
            size="sm"
            className={`col-span-2 sm:col-span-1 text-xs sm:text-sm ${selectedPeriod === 'custom' ? 'bg-indigo-600 hover:bg-indigo-700' : ''}`}
          >
            <Calendar size={16} className="mr-1.5" />
            {selectedPeriod === 'custom' && dateFrom && dateTo
              ? getFilterLabel()
              : 'Personalizado'}
          </Button>
        </div>

        {showDateRange && selectedPeriod === 'custom' && (
          <div className="flex flex-wrap items-end gap-4 p-4 bg-muted rounded-lg border border-border">
            <div>
              <Label className="text-sm font-medium text-foreground mb-1.5 block">De</Label>
              <Input
                type="date"
                data-testid="date-from-input"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-[180px]"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-foreground mb-1.5 block">Até</Label>
              <Input
                type="date"
                data-testid="date-to-input"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-[180px]"
              />
            </div>
          </div>
        )}
      </div>

      {/* KPI Cards - Main Row */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mb-4">
        <Card className="hover:shadow-md transition-shadow" data-testid="kpi-income">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground">Entradas</CardTitle>
            <div className="flex items-center gap-1">
              <HelpTip text="Total de vendas e receitas recebidas no período selecionado." />
              <TrendingUp className="text-emerald-600" size={18} />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {formatCurrency(adjustedIncome)}
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow" data-testid="kpi-expense">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground">
              {operationalMode ? 'Saídas Operacionais' : 'Saídas'}
            </CardTitle>
            <div className="flex items-center gap-1">
              <HelpTip text={operationalMode ? "Gastos do negócio sem contar retiradas pessoais (saques)." : "Total de gastos e despesas no período selecionado."} />
              <TrendingDown className="text-rose-600" size={18} />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {formatCurrency(displayExpense)}
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow" data-testid="kpi-profit">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground">Lucro Líquido</CardTitle>
            <div className="flex items-center gap-1">
              <HelpTip text="Resultado final: entradas menos saídas. Se estiver verde, você lucrou. Se vermelho, teve prejuízo." />
              <DollarSign className="text-indigo-600" size={18} />
            </div>
          </CardHeader>
          <CardContent>
            <div className={`text-xl sm:text-2xl font-bold ${adjustedProfit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
              {formatCurrency(adjustedProfit)}
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow" data-testid="kpi-margin">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground">Margem</CardTitle>
            <div className="flex items-center gap-1">
              <HelpTip text="Porcentagem do lucro em relação às entradas. Quanto maior, mais saudável o negócio." />
              <Percent className="text-indigo-600" size={18} />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {adjustedMargin.toFixed(1)}%
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow col-span-2 lg:col-span-1 border-amber-200 dark:border-amber-800" data-testid="kpi-pending">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-amber-600">Valor Pendente</CardTitle>
            <div className="flex items-center gap-1">
              <HelpTip text="Simule um valor extra que você espera receber. Ele é somado às entradas apenas para visualização, sem alterar seus dados reais." />
              {pendingValue && (
                <button
                  onClick={() => setPendingValue('')}
                  data-testid="pending-clear-button"
                  className="p-1 rounded text-amber-400 hover:text-amber-600 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
                >
                  <X size={14} />
                </button>
              )}
              <DollarSign className="text-amber-500" size={18} />
            </div>
          </CardHeader>
          <CardContent>
            <Input
              type="number"
              step="0.01"
              data-testid="pending-value-input"
              value={pendingValue}
              onChange={(e) => setPendingValue(e.target.value)}
              placeholder="0,00"
              className="text-xl sm:text-2xl font-bold h-auto p-0 border-0 shadow-none focus-visible:ring-0 text-amber-600 bg-transparent"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            />
            {pendingAmount > 0 && (
              <p className="text-xs text-amber-500 mt-1">Simulação ativa</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Excluded Categories Cards - Separate row when operational mode is active */}
      {operationalMode && excludedCategories.length > 0 && (
        <div className="mb-6 sm:mb-8">
          <div className={`grid ${excludedCategories.length === 1 ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'} gap-3`}>
            {excludedCategories.map(cat => (
              <Card key={cat.category_id} className="border-violet-200 dark:border-violet-800 bg-violet-50/50 dark:bg-violet-900/10" data-testid={`kpi-excluded-${cat.category_id}`}>
                <CardContent className="flex items-center justify-between py-4 px-5">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: cat.color }} />
                    <p className="text-sm font-medium text-violet-600 dark:text-violet-400 truncate">{cat.name}</p>
                  </div>
                  <div className="text-lg sm:text-xl font-bold text-violet-600 dark:text-violet-400 flex-shrink-0 ml-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {formatCurrency(cat.value)}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-2">Retiradas de lucro — não contabilizadas como despesa operacional</p>
        </div>
      )}

      {!operationalMode && <div className="mb-4" />}

      {/* Charts */}
      <Card data-testid="chart-monthly-trend" className="mb-6">
        <CardHeader>
          <CardTitle>{operationalMode ? 'Tendência Mensal (Operacional)' : 'Tendência Mensal'}</CardTitle>
          <HelpTip text="Mostra a evolução das entradas e saídas mês a mês. Use para identificar tendências de crescimento ou queda." />
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart
              data={(stats?.monthly_trend || []).map(m => operationalMode
                ? { ...m, expense: Math.round((m.expense - (m.saques || 0)) * 100) / 100 }
                : m
              )}
              margin={{ left: -20, right: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 12 }} />
              <YAxis tick={{ fill: '#64748b', fontSize: 12 }} width={60} />
              <Tooltip
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                formatter={(value) => formatCurrency(value)}
              />
              <Legend />
              <Bar dataKey="income" fill={CHART_COLORS.income} name="Entradas" radius={[8, 8, 0, 0]} />
              <Bar dataKey="expense" fill={CHART_COLORS.expense} name={operationalMode ? 'Saídas Operacionais' : 'Saídas'} radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card data-testid="chart-expenses" className="mb-6">
        <CardHeader>
          <CardTitle>{operationalMode ? 'Gastos Operacionais por Categoria' : 'Gastos por Categoria'}</CardTitle>
          <HelpTip text="Distribuição dos seus gastos divididos por categoria. Ajuda a identificar onde você mais gasta." />
        </CardHeader>
        <CardContent>
          {(() => {
            const expData = operationalMode
              ? (stats?.expenses_by_category || []).filter(c => !c.exclude_from_operational)
              : (stats?.expenses_by_category || []);
            return expData.length > 0 ? (
              <div>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie data={expData} cx="50%" cy="50%" labelLine={false} label={false} outerRadius={100} fill="#8884d8" dataKey="value">
                      {expData.map((entry) => (
                        <Cell key={`cell-exp-${entry.name}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 justify-center">
                  {expData.map((entry) => (
                    <div key={`exp-${entry.name}`} className="flex items-center gap-2 text-sm">
                      <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: entry.color }} />
                      <span className="text-muted-foreground break-words">{entry.name}: {formatCurrency(entry.value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center py-16 text-muted-foreground">
                Nenhum gasto registrado
              </div>
            );
          })()}
        </CardContent>
      </Card>

      <Card data-testid="chart-income" className="mb-6">
        <CardHeader>
          <CardTitle>Entradas por Categoria</CardTitle>
          <HelpTip text="Distribuição das suas receitas divididas por categoria. Mostra de onde vem seu faturamento." />
        </CardHeader>
        <CardContent>
          {stats?.income_by_category?.length > 0 ? (
            <div>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={stats.income_by_category}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={false}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {stats.income_by_category.map((entry) => (
                      <Cell key={`cell-inc-${entry.name}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 justify-center">
                {stats.income_by_category.map((entry) => (
                  <div key={`inc-${entry.name}`} className="flex items-center gap-2 text-sm">
                    <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: entry.color }} />
                    <span className="text-muted-foreground break-words">{entry.name}: {formatCurrency(entry.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              Nenhuma entrada registrada
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
